import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Typography, Spin } from "antd";
import { MinusOutlined, LoadingOutlined } from "@ant-design/icons";
import { formatCurrency, getPortfolioTrend } from "../../services/dashboardService";

function LineChart({ monthlyDividendData }) {
  const { Title, Paragraph } = Typography;
  const [portfolioTrendData, setPortfolioTrendData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolioTrend();
  }, []);

  const fetchPortfolioTrend = async () => {
    try {
      setLoading(true);
      const response = await getPortfolioTrend(365); // 1년 데이터로 확장
      setPortfolioTrendData(response);
    } catch (error) {
      console.error('Error fetching portfolio trend:', error);
      setPortfolioTrendData(null);
    } finally {
      setLoading(false);
    }
  };

  // 포트폴리오 데이터 생성 (월별 배당금 우선, 포트폴리오는 월말 기준)
  const generatePortfolioData = () => {
    let dates = [];
    let portfolioValues = [];
    let dividendValues = [];
    
    // 월별 배당금 데이터를 기준으로 차트 구성
    if (monthlyDividendData && monthlyDividendData.categories && monthlyDividendData.amounts) {
      dates = monthlyDividendData.categories; // YYYY-MM 형식
      dividendValues = monthlyDividendData.amounts; // 실제 배당금 데이터
      
      // console.log('Monthly dividend data:', { dates, dividendValues }); // 디버깅용
      
      // 각 월의 포트폴리오 가치 계산
      portfolioValues = dates.map((monthKey, index) => {
        // 실제 포트폴리오 데이터에서 해당 월의 마지막 날 데이터 찾기
        if (portfolioTrendData && portfolioTrendData.success && portfolioTrendData.data) {
          // 해당 월의 데이터들 필터링 (hasValidData가 true인 실제 데이터만)
          const monthData = portfolioTrendData.data.filter(item => 
            item.date.startsWith(monthKey) && item.hasValidData
          );
          
          if (monthData.length > 0) {
            // 해당 월의 마지막 날 실제 데이터 사용
            const lastDayData = monthData[monthData.length - 1];
            return lastDayData.total_assets || lastDayData.total_market_value || 0;
          }
        }
        
        // 실제 데이터가 없으면 0 반환 (샘플 데이터 사용 안 함)
        return 0;
      });
    } 
    // 배당금 데이터가 없는 경우 기본 샘플 데이터
    else {
      // 최근 12개월 데이터 생성
      for (let i = 11; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        dates.push(date.toISOString().slice(0, 7)); // YYYY-MM 형식
        
        // 포트폴리오 값은 0으로 초기화 (실제 데이터가 없으면 표시 안 함)
        portfolioValues.push(0);
        
        // 샘플 배당금 (현실적인 범위)
        dividendValues.push(Math.random() * 1500000 + 500000); // 50만~200만원
      }
    }
    
    return { dates, portfolioValues, dividendValues };
  };

  const { dates, portfolioValues, dividendValues } = generatePortfolioData();

  // 차트 옵션 설정
  const chartOptions = {
    chart: {
      height: 350,
      type: 'area',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        style: {
          colors: '#8c8c8c',
          fontSize: '12px',
        },
        formatter: function(value) {
          // 날짜를 MM월로 표시
          const date = new Date(value);
          return `${date.getMonth() + 1}월`;
        },
      },
    },
    yaxis: [
      {
        title: {
          text: '포트폴리오 가치 (원)',
          style: {
            color: '#8c8c8c',
          },
        },
        labels: {
          style: {
            colors: '#8c8c8c',
          },
          formatter: function (val) {
            if (val >= 100000000) {
              return `${(val / 100000000).toFixed(1)}억`;
            } else if (val >= 10000000) {
              return `${(val / 10000000).toFixed(1)}천만`;
            } else {
              return `${(val / 10000).toFixed(0)}만`;
            }
          },
        },
      },
      {
        opposite: true,
        title: {
          text: '월별 배당금 (원)',
          style: {
            color: '#8c8c8c',
          },
        },
        labels: {
          style: {
            colors: '#8c8c8c',
          },
          formatter: function (val) {
            return `${(val / 10000).toFixed(0)}만`;
          },
        },
      },
    ],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 90, 100],
      },
    },
    tooltip: {
      y: [
        {
          formatter: function (val) {
            return formatCurrency(val);
          },
        },
        {
          formatter: function (val) {
            return formatCurrency(val);
          },
        },
      ],
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    colors: ['#1890ff', '#52c41a'],
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
    },
  };

  // ApexCharts용 안전한 데이터 형태로 변환
  // "YYYY-MM" 문자열을 Unix 타임스탬프(ms)로 변환하여 datetime 축과 충돌 방지
  const toTimestamp = (dateStr) => {
    if (!dateStr) return null;
    // "YYYY-MM" → "YYYY-MM-01"로 변환 후 UTC 타임스탬프 추출
    const fullDate = dateStr.length === 7 ? dateStr + '-01' : dateStr;
    const ts = new Date(fullDate).getTime();
    return isNaN(ts) ? null : ts;
  };

  const chartSeries = [
    {
      name: '포트폴리오 가치',
      type: 'area',
      data: portfolioValues
        .map((value, index) => ({ x: toTimestamp(dates[index]), y: Number(value) || 0 }))
        .filter(point => point.x !== null),
    },
    {
      name: '월별 배당금',
      type: 'line',
      data: dividendValues
        .map((value, index) => ({ x: toTimestamp(dates[index]), y: Number(value) || 0 }))
        .filter(point => point.x !== null),
    },
  ];

  // 성과 계산 (월별 기준)
  const currentValue = portfolioValues[portfolioValues.length - 1] || 0;
  const previousValue = portfolioValues[portfolioValues.length - 2] || currentValue;
  const monthlyChange = currentValue - previousValue;
  const monthlyChangePercent = previousValue !== 0 ? ((monthlyChange / previousValue) * 100) : 0;

  // 이번 달 배당금 확인
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const currentMonthIndex = dates.indexOf(currentMonth);
  const currentMonthDividend = currentMonthIndex !== -1 ? dividendValues[currentMonthIndex] : 0;

  // 데이터 소스 표시
  const hasRealPortfolioData = portfolioTrendData && portfolioTrendData.success && 
    portfolioTrendData.data && portfolioTrendData.data.some(d => d.hasValidData);
  const hasRealDividendData = monthlyDividendData && monthlyDividendData.amounts;
  
  let dataSource = "";
  if (hasRealPortfolioData && hasRealDividendData) {
    dataSource = "실제 데이터";
  } else if (hasRealDividendData && !hasRealPortfolioData) {
    dataSource = "배당금: 실제, 포트폴리오: 데이터 없음 (새로고침 필요)";
  } else if (!hasRealDividendData && hasRealPortfolioData) {
    dataSource = "포트폴리오: 실제, 배당금: 데이터 없음";
  } else {
    dataSource = "데이터 없음 (새로고침 필요)";
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        <div style={{ marginTop: 16, color: '#666' }}>포트폴리오 데이터 로딩 중...</div>
      </div>
    );
  }

  return (
    <>
      <div className="linechart">
        <div>
          <Title level={5}>포트폴리오 성과</Title>
          <Paragraph className="lastweek">
            전월 대비 <span className={monthlyChangePercent >= 0 ? "bnb2" : "redtext"}>
              {monthlyChangePercent >= 0 ? '+' : ''}{monthlyChangePercent.toFixed(2)}%
            </span>
            {currentMonthDividend > 0 && (
              <span style={{ marginLeft: '16px', color: '#52c41a' }}>
                이번 달 배당금: {formatCurrency(currentMonthDividend)}
              </span>
            )}
          </Paragraph>
          <Paragraph style={{ fontSize: '12px', color: '#999', margin: 0 }}>
            ({dataSource})
          </Paragraph>
        </div>
        <div className="sales">
          <ul>
            <li><MinusOutlined style={{ color: '#1890ff' }} /> 포트폴리오 가치 (월말 기준)</li>
            <li><MinusOutlined style={{ color: '#52c41a' }} /> 월별 배당금</li>
          </ul>
        </div>
      </div>

      <ReactApexChart
        className="full-width"
        options={chartOptions}
        series={chartSeries}
        type="area"
        height={350}
        width={"100%"}
      />
    </>
  );
}

export default LineChart;
